import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import has from 'lodash/has';

import gettext from '@eb/gettext';
import { SelectField } from '@eb/eds-input-field';
import { SearchFilter } from '@eb/eds-search-filter';
import { REQUEST_SHAPE_PROP_TYPE } from '@eb/site-structure';
import { joinPath } from '@eb/path-utils';

import {
    DEFAULT_SEARCH_QUERY,
    DEFAULT_PAGE_NUMBER,
    DEFAULT_PAGE_SIZE,
    DEFAULT_ORGANIZER_ID,
    EVENTS_URL_PATH,
    STATUS_FILTERS,
    SELECT_FIELD_FILTER_OPTIONS,
    STATUS_FILTER_PROP_TYPE,
    STATUS_FILTER_DRAFT,
    STATUS_FILTER_PAST,
    STATUS_FILTER_PUBLISHED,
    STATUS_FILTER_ALL,
    STATUS_COUNTS_PUBLISHED,
    STATUS_COUNTS_DRAFT,
    STATUS_COUNTS_PAST,
    ORGANIZER_SHAPE,
} from '../constants';
import {
    filterEvents,
    fetchStatusCounts,
    fetchAllOrganizers,
} from '../actions';
import { parseEventsParams } from '../utils';

export class EventsFilterGroup extends PureComponent {
    static propTypes = {
        baseUrl: PropTypes.string.isRequired,
        organizationId: PropTypes.string.isRequired,
        status: STATUS_FILTER_PROP_TYPE.isRequired,
        location: PropTypes.shape({
            pathname: PropTypes.string,
            query: PropTypes.object,
        }).isRequired,
        request: REQUEST_SHAPE_PROP_TYPE,
        onLoadStatusCounts: PropTypes.func,
        onLoadOrganizers: PropTypes.func,
        onFilterEvents: PropTypes.func,
        organizers: PropTypes.arrayOf(ORGANIZER_SHAPE),
        searchQuery: PropTypes.string,
        organizerId: PropTypes.string,
        statusCounts: PropTypes.object,
        pageSize: PropTypes.number,
        /**
         * Disables the logic to fetch and pre-fill values on the form to save API
         * calls where they are not necessary.
         */
        disableFilterGroupDefaultValues: PropTypes.bool,
    };

    static defaultProps = {
        searchQuery: DEFAULT_SEARCH_QUERY,
        organizerId: DEFAULT_ORGANIZER_ID,
        pageSize: DEFAULT_PAGE_SIZE,
    };

    constructor(props) {
        super(props);

        this.state = {
            searchQuery: props.searchQuery,
        };
    }

    componentDidMount() {
        if (!this.props.disableFilterGroupDefaultValues) {
            // if we're doing SSR and if we are not passing in an onLoadStatusCounts
            // functions then skip the status count call since it won't be used
            if (!this._isSSR() && this.props.onLoadStatusCounts) {
                this._loadStatusCounts();
            }

            this._loadOrganizers();
        }
    }

    componentDidUpdate(prevProps) {
        const { onLoadStatusCounts, statusCounts } = this.props;
        const { statusCounts: prevStatusCounts } = prevProps;

        if (onLoadStatusCounts && !isEqual(statusCounts, prevStatusCounts)) {
            this._getDefaultStatusFilter(statusCounts);
        }
    }

    _isSSR() {
        return !!STATUS_FILTERS.find((status) =>
            this.props.request.path.includes(status),
        );
    }

    _getDefaultStatusFilter(statusCounts) {
        // get only the statuses that have a count greater than 0
        const filteredStatuses = Object.keys(statusCounts).filter(
            (statusType) => statusCounts[statusType] > 0,
        );

        let defaultStatus;

        // if filteredStatuses is empty then every status is at zero so
        // default to All
        if (isEmpty(filteredStatuses)) {
            defaultStatus = STATUS_FILTER_ALL;
        }

        // otherwise we want to go in order of
        // published -> draft -> past -> all
        else if (filteredStatuses.includes(STATUS_COUNTS_PUBLISHED)) {
            defaultStatus = STATUS_FILTER_PUBLISHED;
        } else if (filteredStatuses.includes(STATUS_COUNTS_DRAFT)) {
            defaultStatus = STATUS_FILTER_DRAFT;
        } else if (filteredStatuses.includes(STATUS_COUNTS_PAST)) {
            defaultStatus = STATUS_FILTER_PAST;
        }

        // set the filter to the correct status
        this._handleStatusFilter(defaultStatus);
    }

    _loadOrganizers = () => {
        const { organizers, onLoadOrganizers, organizationId } = this.props;

        // NOTE: Undefined `organizers` signifies that it hasn't
        // been loaded yet, which would be different from empty
        // which signifies that they have been loaded but there
        // aren't any.
        if (!organizers && onLoadOrganizers) {
            onLoadOrganizers(organizationId);
        }
    };

    _loadStatusCounts = () => {
        const { organizationId, onLoadStatusCounts, statusCounts } = this.props;

        // only get status counts if it's undefined or they are empty.
        if (!statusCounts || isEmpty(statusCounts)) {
            onLoadStatusCounts(organizationId);
        }
    };

    _renderOrganizerFilter = () => {
        const { organizerId, organizers } = this.props;

        // We only need to display the filter if there is more than one organizer
        // to filter on
        if (!organizers || organizers.length < 2) {
            return null;
        }

        const organizerValues = organizers.map(({ id, name }) => ({
            value: id,
            display: name || gettext('Unnamed Organizer'),
        }));
        const organizerSelectValues = [
            { value: DEFAULT_ORGANIZER_ID, display: gettext('All') },
            ...organizerValues,
        ];

        return (
            <div
                className="event-screen-selected-field-filter eds-g-cell eds-g-cell-12-12 eds-g-cell-sw-4-12 eds-l-sn-pad-left-0 eds-l-sm-pad-left-0 eds-l-pad-left-2"
                data-spec="event-screen-organizer-filter-shell"
            >
                <SelectField
                    id="event-organizer-filter"
                    label={gettext('Organizer')}
                    value={organizerId}
                    values={organizerSelectValues}
                    onChange={this._handleOrganizerFilter}
                    defaultValue={DEFAULT_ORGANIZER_ID}
                    borderType="default"
                />
            </div>
        );
    };

    _handleFilter = (filterParams) => {
        const {
            onFilterEvents,
            status,
            pageSize,
            organizerId,
            searchQuery,
            baseUrl,
        } = this.props;
        const hasSearchFilter = has(filterParams, 'searchQuery');
        // If a searchQuery is passed as a filterParam we need to honor it as in the case
        // when the search field is cleared, otherwise we use the query from state
        const filterSearchTerm = hasSearchFilter
            ? filterParams.searchQuery
            : searchQuery;

        // NOTE: Effectively what will happen is that `onFilterEvents` will do a client-side
        // redirect to the events page w/ the updated filterParams. This will trigger
        // componentDidUpdate above, which will cause new events matching the params to load.
        // This makes the route the source of truth
        onFilterEvents(baseUrl, {
            status,
            pageSize,
            pageNumber: DEFAULT_PAGE_NUMBER,
            searchQuery: filterSearchTerm,
            organizerId,
            ...filterParams,
        });

        if (hasSearchFilter) {
            // Update state with the searchQuery from filterParams to handle
            // the case when the search field is cleared
            this._handleSearchChange({ searchQuery: filterParams.searchQuery });
        }
    };

    _handleStatusFilter = (status) => {
        this._handleFilter({ status });
    };

    _handleOrganizerFilter = (organizerId) => {
        this._handleFilter({ organizerId });
    };

    _handleSearchChange = ({ searchQuery }) => {
        this.setState({ searchQuery });
    };

    render() {
        const { status } = this.props;
        const { searchQuery } = this.state;
        const organizerFilter = this._renderOrganizerFilter();
        const searchFilterColumns = organizerFilter ? 4 : 6;

        return (
            <div className="eds-l-mar-vert-8">
                <div className="eds-g-grid">
                    <div
                        className={`eds-g-cell eds-g-cell-12-12 eds-g-cell-sw-${searchFilterColumns}-12`}
                        data-spec="events-screen-search-filter-shell"
                    >
                        <SearchFilter
                            searchQuery={searchQuery}
                            onChange={this._handleSearchChange}
                            onFilter={this._handleFilter}
                            inputLabel={gettext('Search events')}
                            inputPlaceholder={gettext('Search events')}
                        />
                    </div>
                    <div
                        className="event-screen-selected-field-filter eds-g-cell eds-g-cell-12-12 eds-g-cell-sw-4-12 eds-l-sn-pad-left-0 eds-l-sm-pad-left-0 eds-l-pad-left-2"
                        data-spec="event-screen-status-filter-shell"
                    >
                        <SelectField
                            id="event-status-filter"
                            label={gettext('Event status')}
                            value={status}
                            values={SELECT_FIELD_FILTER_OPTIONS}
                            onChange={this._handleStatusFilter}
                            defaultValue={status}
                            borderType="default"
                        />
                    </div>
                    {organizerFilter}
                </div>
            </div>
        );
    }
}

export default connect(
    (
        { organizationId, activeOrganizationId, statusCounts },
        { location = {}, disableFilterGroupDefaultValues },
    ) => {
        let paramsFromRequest;

        if (disableFilterGroupDefaultValues) {
            // If default filter logic is disabled, default to these values.
            paramsFromRequest = { status: STATUS_FILTER_ALL };
        } else {
            paramsFromRequest = parseEventsParams(
                EVENTS_URL_PATH,
                location.pathname,
                location.query,
            );
        }

        return {
            ...paramsFromRequest,
            organizationId: organizationId || activeOrganizationId,
            statusCounts: statusCounts,
        };
    },
    {
        onLoadStatusCounts: (organizationId) =>
            fetchStatusCounts(organizationId),
        onLoadOrganizers: fetchAllOrganizers,
        onFilterEvents: (baseUrl, params) =>
            filterEvents(joinPath(baseUrl, EVENTS_URL_PATH), params),
    },
)(EventsFilterGroup);
